import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import CountdownCard from "../components/CountdownCard";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle
} from "../components/ui/alert-dialog";
import {
  Users, GraduationCap, Books, CreditCard, ChartLineUp, CheckCircle, Trophy, Clock,
  CurrencyDollar, Certificate, VideoCamera, ArrowUp, ArrowDown, Minus, X, FileText
} from "@phosphor-icons/react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, color = "bg-blue-800", subtext }) {
  return (
    <Card className="border border-slate-200 rounded-none shadow-sm hover:border-slate-400 hover:shadow-md transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</p>
            <p className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
            {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
          </div>
          <div className={`${color} p-2 text-white`}>
            <Icon size={20} weight="bold" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminDashboard({ stats, userRole, pageViews }) {
  const isSuperAdmin = userRole === "super_admin";
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Primary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Students" value={stats.total_students || 0} subtext={`${stats.total_instructors || 0} instructors`} />
        <StatCard icon={GraduationCap} label="Programmes" value={stats.total_programmes || 0} color="bg-slate-900" />
        <StatCard icon={Books} label="Courses" value={stats.total_courses || 0} color="bg-emerald-600" />
        {isSuperAdmin && <StatCard icon={CurrencyDollar} label="Total Revenue" value={`$${(stats.total_revenue || 0).toLocaleString()}`} color="bg-amber-500" subtext={stats.pending_revenue > 0 ? `$${stats.pending_revenue.toLocaleString()} pending` : null} />}
        {!isSuperAdmin && <StatCard icon={CheckCircle} label="Active Enrollments" value={stats.active_enrollments || 0} color="bg-emerald-600" />}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats.total_users || 0} />
        <StatCard icon={CheckCircle} label="Active Enrollments" value={stats.active_enrollments || 0} color="bg-emerald-600" subtext={`${stats.total_enrollments || 0} total`} />
        <StatCard icon={Certificate} label="Certificates Issued" value={stats.total_certificates || 0} color="bg-blue-600" />
        <StatCard icon={VideoCamera} label="Meetings Created" value={stats.total_meetings || 0} color="bg-indigo-600" />
      </div>

      {/* Analytics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Recent Enrollments */}
        <Card className="border border-slate-200 rounded-none shadow-sm" data-testid="recent-enrollments">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4">Recent Enrollments</h3>
            {stats.recent_enrollments?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_enrollments.map((e, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-800/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-blue-800">
                      {(e.user_name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{e.user_name || e.user_email}</p>
                      <p className="text-xs text-slate-500 truncate">{e.programme_title}</p>
                    </div>
                    <Badge variant={e.status === "active" ? "default" : "secondary"} className="rounded-none text-[10px] capitalize flex-shrink-0">
                      {e.status?.replace("_", " ")}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No enrollments yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4 flex-wrap">
        <Link to="/programmes" className="bg-blue-800 text-white px-5 py-2.5 text-sm font-medium hover:bg-blue-900 transition-all" data-testid="admin-manage-programmes">
          Manage Programmes
        </Link>
        <Link to="/courses" className="bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 transition-all" data-testid="admin-manage-courses">
          Manage Courses
        </Link>
        {isSuperAdmin && (
          <Link to="/admin/users" className="border border-slate-200 text-slate-700 px-5 py-2.5 text-sm font-medium hover:bg-slate-100 transition-all" data-testid="admin-manage-users">
            Manage Users
          </Link>
        )}
        <Link to="/payments" className="border border-slate-200 text-slate-700 px-5 py-2.5 text-sm font-medium hover:bg-slate-100 transition-all" data-testid="admin-view-payments">
          View Payments
        </Link>
        <Link to="/admin" className="bg-amber-400 text-slate-950 px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-amber-500 transition-all inline-flex items-center gap-2" data-testid="admin-open-cms">
          <span>Public-Site CMS</span>
          <span className="text-[10px] bg-slate-950 text-amber-400 px-1.5 py-0.5">/admin</span>
        </Link>
      </div>
    </div>
  );
}

function InstructorDashboard({ stats }) {
  return (
    <div className="space-y-8" data-testid="instructor-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Books} label="My Courses" value={stats.my_courses || 0} />
        <StatCard icon={Users} label="My Students" value={stats.total_students || 0} color="bg-emerald-600" />
        <StatCard icon={Trophy} label="Quizzes Created" value={stats.total_quizzes || 0} color="bg-amber-500" />
        <StatCard icon={Clock} label="Pending Reviews" value={stats.pending_submissions || 0} color="bg-slate-900" />
      </div>
      <div className="flex gap-4 flex-wrap">
        <Link to="/courses" className="bg-blue-800 text-white px-5 py-2.5 text-sm font-medium hover:bg-blue-900 transition-all" data-testid="instructor-my-courses">
          My Courses
        </Link>
        <Link to="/ai-tools" className="border border-slate-200 text-slate-700 px-5 py-2.5 text-sm font-medium hover:bg-slate-100 transition-all" data-testid="instructor-ai-tools">
          AI Tools
        </Link>
      </div>
    </div>
  );
}

function StudentDashboard({ stats, user }) {
  const [enrollments, setEnrollments] = useState([]);
  const [progressData, setProgressData] = useState([]);
  const [freeCourses, setFreeCourses] = useState([]);
  const [partner, setPartner] = useState(null);
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [admissionLetter, setAdmissionLetter] = useState(null);
  const [pendingProgrammes, setPendingProgrammes] = useState([]);
  const [waitingForStart, setWaitingForStart] = useState(null);

  const isFreeOnly = user?.interest_type === "free_course" || user?.access_level === "free_only";
  const hasPaidProgramme = user?.has_paid_programme || user?.access_level === "programme";

  const fetchEnrollments = () => {
    api.get("/enrollments/my").then(r => setEnrollments(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchEnrollments();
    api.get("/progress/my").then(r => setProgressData(r.data)).catch(() => {});
    api.get("/courses/free").then(r => setFreeCourses(r.data)).catch(() => {});
    api.get("/accountability/my").then(r => setPartner(r.data?.partner)).catch(() => {});
    if (!isFreeOnly) {
      api.get("/admission-letter/me").then(r => setAdmissionLetter(r.data && r.data.filename ? r.data : null)).catch(() => {});
    }
    // Section 1 + 2 — only show the student's own enrollments + countdown card if approved
    api.get("/access/my-enrollments").then(r => {
      const list = r.data || [];
      const waiting = list.find(e => e.access?.reason === "waiting_for_start");
      if (waiting) setWaitingForStart(waiting);
      const pending = list.filter(e => ["pending_payment", "pending_verification", "applied"].includes(e.access?.enrollment_status));
      setPendingProgrammes(pending);
    }).catch(() => {});
  }, []);

  const handleWithdraw = async () => {
    if (!withdrawTarget) return;
    setWithdrawing(true);
    try {
      await api.delete(`/enrollments/${withdrawTarget.id}/withdraw`);
      toast.success("Interest withdrawn. You are no longer enrolled in this programme.");
      setWithdrawTarget(null);
      fetchEnrollments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not withdraw interest");
    }
    setWithdrawing(false);
  };

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      {/* Section A3 — Document shortcuts for paid programme students */}
      {hasPaidProgramme && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="student-doc-shortcuts">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admission-letter/me/file`}
            target="_blank"
            rel="noopener noreferrer"
            className={`group bg-white border ${admissionLetter ? "border-blue-200 hover:border-blue-700" : "border-slate-200 opacity-60 pointer-events-none"} p-5 transition-all`}
            data-testid="dash-admission-letter"
          >
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 text-blue-800 w-10 h-10 flex items-center justify-center"><FileText size={20} weight="fill" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Admission</p>
                <p className="font-semibold text-sm text-slate-950 truncate">{admissionLetter ? "View Admission Letter" : "Admission letter pending"}</p>
                {admissionLetter && <p className="text-[10px] text-slate-400">{admissionLetter.filename}</p>}
              </div>
            </div>
          </a>
          <Link to="/transcripts" className="group bg-white border border-slate-200 hover:border-blue-700 p-5 transition-all" data-testid="dash-transcript-shortcut">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 text-emerald-800 w-10 h-10 flex items-center justify-center"><FileText size={20} weight="fill" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Records</p>
                <p className="font-semibold text-sm text-slate-950 truncate">Download Transcript</p>
              </div>
            </div>
          </Link>
          <Link to="/certificates" className="group bg-white border border-slate-200 hover:border-blue-700 p-5 transition-all" data-testid="dash-certificate-shortcut">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 text-amber-800 w-10 h-10 flex items-center justify-center"><FileText size={20} weight="fill" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Achievement</p>
                <p className="font-semibold text-sm text-slate-950 truncate">Download Certificate</p>
              </div>
            </div>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={GraduationCap} label="Enrolled Programmes" value={stats.enrolled_programmes || 0} />
        <StatCard icon={CheckCircle} label="Active" value={stats.active_programmes || 0} color="bg-emerald-600" />
        <StatCard icon={Books} label="Lessons Completed" value={stats.lessons_completed || 0} color="bg-slate-900" />
        <StatCard icon={ChartLineUp} label="Avg Quiz Score" value={`${stats.avg_quiz_score || 0}%`} color="bg-amber-500" />
      </div>

      {/* My Enrolled Programmes */}
      {enrollments.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            My Programmes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {enrollments.map((e) => {
              const isPaid = e.status === "active" && e.balance <= 0;
              const isPending = e.status === "pending_payment";
              const hasDeadline = isPending && e.payment_deadline;
              const isOverdue = e.balance_overdue === true;
              const showWarning = e.balance_warning === true;
              let daysLeft = null;
              if (hasDeadline) {
                const dl = new Date(e.payment_deadline);
                daysLeft = Math.max(0, Math.ceil((dl - new Date()) / (1000 * 60 * 60 * 24)));
              }
              const balanceDaysLeft = typeof e.balance_days_remaining === "number" ? e.balance_days_remaining : null;
              // Programme start countdown — backend exposes programme.start_date on enrolment.
              const startDate = e.programme?.start_date ? new Date(e.programme.start_date) : null;
              const daysUntilStart = startDate ? Math.ceil((startDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
              const waitingForStart = e.status === "active" && daysUntilStart !== null && daysUntilStart > 0;
              // Card is clickable only when the student can actually open the programme.
              const isClickable = !isPending && !waitingForStart;
              const cardClasses = `border rounded-none shadow-sm transition-all ${isClickable ? "cursor-pointer hover:shadow-md" : "cursor-default"} ${isOverdue ? "border-red-400 bg-red-50/40" : showWarning ? "border-amber-400 bg-amber-50/40" : isPending ? "border-amber-300 bg-amber-50/30" : waitingForStart ? "border-blue-200 bg-blue-50/30" : "border-slate-200"}`;
              const inner = (
                    <Card className={cardClasses} data-testid={`enrollment-card-${e.id}`}>
                      <CardContent className="p-5">
                        <h4 className="font-semibold text-slate-900 mb-1 pr-8">{e.programme?.title || e.programme_title || "Programme"}</h4>
                        {e.admission_number && (
                          <p className="text-[11px] text-blue-900 mb-2 font-mono" data-testid={`enrollment-admission-no-${e.id}`}>
                            Admission No: <b>{e.admission_number}</b>
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <Badge className={`rounded-none text-xs ${isPaid ? "bg-emerald-600 text-white" : isOverdue ? "bg-red-600 text-white" : waitingForStart ? "bg-blue-700 text-white" : isPending ? "bg-amber-500 text-white" : ""}`}>
                            {isPaid ? "Paid" : isOverdue ? "Access Locked" : waitingForStart ? "Starts Soon" : e.status?.replace("_", " ")}
                          </Badge>
                          {e.balance > 0 && (
                            <span className={`font-medium ${isOverdue ? "text-red-700" : "text-amber-600"}`}>Balance: ${e.balance?.toFixed(2)}</span>
                          )}
                        </div>
                        {waitingForStart && (
                          <div className="mt-2 text-xs text-blue-900 bg-blue-100 px-3 py-2 border border-blue-200" data-testid={`waiting-for-start-${e.id}`}>
                            <strong>Programme starts in {daysUntilStart} day{daysUntilStart === 1 ? "" : "s"}.</strong> Course materials unlock on {startDate.toLocaleDateString()}.
                          </div>
                        )}
                        {isOverdue && (
                          <div className="mt-2 text-xs text-red-800 bg-red-100 px-3 py-2 border border-red-200" data-testid={`balance-overdue-${e.id}`}>
                            <strong>Access locked.</strong> Your 6-week balance deadline expired {Math.abs(balanceDaysLeft || 0)} day(s) ago. Settle the outstanding ${e.balance?.toFixed(2)} to regain access to course materials.
                          </div>
                        )}
                        {!isOverdue && showWarning && (
                          <div className="mt-2 text-xs text-amber-900 bg-amber-100 px-3 py-2 border border-amber-300" data-testid={`balance-warning-${e.id}`}>
                            <strong>Final reminder:</strong> Only {balanceDaysLeft} day(s) left to clear the ${e.balance?.toFixed(2)} balance. Access will be suspended after the deadline.
                          </div>
                        )}
                        {!isOverdue && !showWarning && e.balance > 0 && balanceDaysLeft !== null && balanceDaysLeft >= 0 && e.status === "active" && (
                          <div className="mt-2 text-xs text-slate-700 bg-slate-100 px-3 py-1.5 border border-slate-200" data-testid={`balance-countdown-${e.id}`}>
                            Balance deadline: {balanceDaysLeft} day(s) remaining (6-week rule)
                          </div>
                        )}
                        {isPending && !isOverdue && (
                          <div className="mt-2 text-xs text-amber-700 bg-amber-100 px-3 py-1.5 border border-amber-200">
                            {daysLeft !== null ? (
                              daysLeft > 0 ? `Payment deadline: ${daysLeft} days remaining` : "Payment overdue — access may be suspended"
                            ) : (
                              "Payment required to access course content"
                            )}
                          </div>
                        )}
                        {isPending && (
                          <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`pending-actions-${e.id}`}>
                            <Link to={`/pay/${e.programme_id}`} onClick={(ev) => ev.stopPropagation()}>
                              <Button size="sm" className="bg-blue-800 hover:bg-blue-900 text-white rounded-none text-xs gap-1" data-testid={`pay-now-${e.id}`}>
                                <CreditCard size={12} weight="bold" /> Pay Now
                              </Button>
                            </Link>
                            <button
                              type="button"
                              onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setWithdrawTarget(e); }}
                              className="text-xs font-medium text-red-700 hover:text-red-900 underline underline-offset-2 inline-flex items-center gap-1"
                              data-testid={`withdraw-interest-${e.id}`}
                            >
                              <X size={12} /> Withdraw interest
                            </button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
              );
              return (
                <div key={e.id} className="relative">
                  {isClickable ? (
                    <Link to={`/programmes/${e.programme_id}`} data-testid={`open-programme-${e.id}`}>{inner}</Link>
                  ) : (
                    <div data-testid={`locked-programme-${e.id}`}>{inner}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Course Progress (only for active/paid enrollments) */}
      {progressData.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Course Progress
          </h3>
          <div className="space-y-3">
            {progressData.map((p) => (
              <Card key={p.course_id} className="border border-slate-200 rounded-none shadow-sm">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.course_title}</p>
                    <p className="text-xs text-slate-500">{p.completed_lessons}/{p.total_lessons} lessons</p>
                  </div>
                  <div className="w-32">
                    <Progress value={p.percentage} className="h-2" />
                  </div>
                  <Badge variant={p.percentage === 100 ? "default" : "secondary"} className="rounded-none text-xs">
                    {p.percentage}%
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Free Courses */}
      {freeCourses.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Free Courses
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {freeCourses.map((c) => (
              <Link key={c.id} to={`/courses/${c.id}`}>
                <Card className="border border-slate-200 rounded-none shadow-sm hover:border-emerald-400 hover:shadow-md transition-all cursor-pointer">
                  <CardContent className="p-5">
                    <Badge className="rounded-none text-[10px] bg-emerald-600 text-white mb-2">Free</Badge>
                    <h4 className="font-semibold text-slate-900 text-sm">{c.title}</h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {enrollments.length === 0 && freeCourses.length === 0 && (
        <div className="text-center py-12">
          <GraduationCap size={48} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">{isFreeOnly ? "Start exploring our free courses!" : "You haven't enrolled in any programmes yet."}</p>
          <Link to={isFreeOnly ? "/free-courses" : "/programmes-catalogue"}>
            <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none text-sm">
              {isFreeOnly ? "Browse Free Courses" : "Browse Programmes"}
            </Button>
          </Link>
        </div>
      )}

      {/* Accountability Partner */}
      {partner && (
        <Card className="border border-emerald-200 bg-emerald-50/30 rounded-none shadow-sm" data-testid="accountability-partner">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-emerald-800 mb-3">Your Accountability Partner</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {partner.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{partner.name}</p>
                <p className="text-xs text-slate-500">{partner.email}</p>
              </div>
            </div>
            <p className="text-xs text-emerald-700 mt-2">You&apos;ve been paired to support each other&apos;s learning journey. Check in regularly!</p>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <div className="flex gap-4 flex-wrap">
        {isFreeOnly ? (
          <>
            <Link to="/free-courses" className="bg-emerald-700 text-white px-5 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-all" data-testid="student-free-courses">
              Browse Free Courses
            </Link>
            <Link to="/programmes-catalogue" className="border border-slate-200 text-slate-700 px-5 py-2.5 text-sm font-medium hover:bg-slate-100 transition-all" data-testid="student-view-programmes">
              View All Programmes
            </Link>
          </>
        ) : (
          <>
            <Link to="/programmes-catalogue" className="bg-blue-800 text-white px-5 py-2.5 text-sm font-medium hover:bg-blue-900 transition-all" data-testid="student-browse-programmes">
              Browse Programmes
            </Link>
            <Link to="/free-courses" className="border border-slate-200 text-slate-700 px-5 py-2.5 text-sm font-medium hover:bg-slate-100 transition-all" data-testid="student-free-courses">
              Free Courses
            </Link>
          </>
        )}
      </div>

      {/* Withdraw Interest Confirmation */}
      <AlertDialog open={!!withdrawTarget} onOpenChange={(open) => !open && setWithdrawTarget(null)}>
        <AlertDialogContent className="rounded-none" data-testid="withdraw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Withdraw your interest?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to withdraw from{" "}
              <strong>{withdrawTarget?.programme?.title || withdrawTarget?.programme_title || "this programme"}</strong>.
              You haven&apos;t made any payment, so no refund is required. You can re-enroll anytime while the programme remains open.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="withdraw-cancel-btn">Keep Enrollment</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleWithdraw}
              disabled={withdrawing}
              className="bg-red-700 text-white hover:bg-red-800 rounded-none"
              data-testid="withdraw-confirm-btn"
            >
              {withdrawing ? "Withdrawing..." : "Yes, Withdraw"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [pageViews, setPageViews] = useState(null);

  const [statsError, setStatsError] = useState(null);
  const role = user?.role || "student";

  useEffect(() => {
    if (role === "pod_subscriber") return; // POD users redirected; skip LMS stats fetch.
    api.get("/dashboard/stats").then(r => { setStats(r.data); setStatsError(null); setLoading(false); }).catch(err => {
      // Surface the failure instead of silently rendering zeros. This catches
      // the production "wrong backend URL" / CORS / network class of bugs.
      const msg = err?.response?.data?.detail || err?.message || "Could not load dashboard data";
      setStatsError(msg);
      setLoading(false);
    });
    if (["super_admin", "admin"].includes(role)) {
      api.get("/analytics/pageviews").then(r => setPageViews(r.data)).catch(() => {});
    }
  }, [role]);

  // POD subscribers belong in /pod/dashboard, not the LMS dashboard.
  // Staff (super_admin/admin/instructor) keep access to the LMS dashboard even if they also have pod_access.
  const isStaff = ["super_admin", "admin", "instructor", "finance"].includes(role);
  if (!isStaff && (role === "pod_subscriber" || user?.pod_access === true || user?.can_access_pod === true)) {
    return <Navigate to="/pod/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="dashboard-page">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Dashboard</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Welcome back, {user?.name}
          </h1>
        </div>

        {statsError && (
          <div className="mb-6 border border-red-300 bg-red-50 p-4" data-testid="dashboard-stats-error">
            <p className="text-sm font-bold text-red-800">Couldn&apos;t load dashboard data</p>
            <p className="text-xs text-red-700 mt-1">{statsError}</p>
            <p className="text-[11px] text-red-600 mt-1">All counters below will show 0 until this is fixed. Most common cause: production frontend pointing at a wrong/stale backend URL.</p>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-100 animate-pulse" />)}
          </div>
        ) : (
          <>
            {["super_admin", "admin"].includes(role) && <AdminDashboard stats={stats} userRole={role} pageViews={pageViews} />}
            {role === "instructor" && <InstructorDashboard stats={stats} />}
            {role === "student" && <StudentDashboard stats={stats} user={user} />}
            {!["super_admin", "admin", "instructor", "student"].includes(role) && <StudentDashboard stats={stats} user={user} />}
          </>
        )}
      </div>
    </Layout>
  );
}
